import { AllyNode, AllyPoint } from "../types";
import { AllyBase } from "./base";
import { AllyId } from "./ally-id.class";
import { AllyFunctionMatrix } from "./ally-function-matrix.class";
import { AllyLibrary } from "./ally-library.class";
import { AllyElements } from "./ally-elements.class";
import { AllyEventListener } from "./ally-event-listener.class";
import { AllyRoot } from "./ally-root.class";
import { AllyAttribute } from "./ally-attribute.class";


/**
 * This class is the most important cornerstone of the De.
 * 
 * Beginning from the root node for each node in the DOM tree a AllyObject is created.
 * 
 * The DOM structure is mirrored.
 * 
 * All functions are implemented by the AllyObject for its individual node of the DOM tree.
 * 
 * @extends AllyBase
 */
export class AllyObject extends AllyBase {

    /**
     * A dummy used only to provide the type for the scope object without additional imports.
     */
    public scope: {self: AllyObject};
    /**
     * The node the AllyObject is managing.
     */
    public node: AllyNode;
    /**
     * The id of the AllyObject.
     * 
     * @see AllyId
     */
    public id: string;
    /**
     * The AllyId object of the AllyObject class.
     * 
     * @see AllyId
     */
    private static ids: AllyId = new AllyId("id", AllyBase.bridge);
    /**
     * A dictionary with predefined entries.
     * 
     * It is used for all permanent variables that are used in the callbacks of the function-matrix.
     * 
     * @see AllyFunctionMatrix
     */
    public variables: {
        subMenu?: AllyObject,
        hostId?: string,
        hiddenText?: Node,
        ariaHidden?: boolean,
        path?: string[]
    } & {[key:string]: unknown};
    /**
     * The list of callbacks that are to be executed if the attributeListener detects an attribute change in this AllyObjects node.
     * 
     * @see AllyRoot
     */
    public attributeListeners: (() => void)[];
    /**
     * The list of callbacks that are to be executed if the childListListener detects a change in the childNode list of this AllyObjects node.
     * 
     * @see AllyRoot
     */
    public childListListeners: (() => void)[];
    /**
     * The list of callbacks that are to be executed if the reset function is called.
     * 
     * These callbacks will also be executed if the destroy function is called.
     */
    public resetFunctions: (() => void)[];
    /**
     * The AllyObject that manages the parent node of this AllyObject objects node.
     * 
     * Note: this may be modified and note represent the actual DOM structure.
     */
    public parentObject: AllyObject | null;
    /**
     * The list of AllyObject objects that manage the child nodes of this AllyObject objects node.
     * 
     * Note: this may be modified and note represent the actual DOM structure.
     */
    public childObjects: AllyObject[];
    /**
     * The AllyFunctionMatrix object for the AllyObject object.
     * 
     * @see AllyFunctionMatrix
     */
    public functionMatrix: AllyFunctionMatrix<AllyObject["scope"]>;
    /**
     * The AllyElements object for the AllyObject class.
     * 
     * @see AllyElements
     */
    private static elements: AllyElements = new AllyElements(AllyBase.bridge);
    /**
     * The AllyAttribute object for the AllyObject object.
     * 
     * @see AllyAttribute
     */
    public attribute: AllyAttribute;
    /**
     * The AllyLibrary object for the AllyObject object.
     * 
     * @see AllyLibrary
     */
    public library: AllyLibrary;
    /**
     * The AllyLibrary object for the AllyObject class.
     * 
     * @see AllyLibrary
     */
    private static library: AllyLibrary = new AllyLibrary(AllyBase.bridge);
    /**
     * The global object for the AllyObject object.
     * 
     * It is shared between all AllyObject objects.
     * 
     * It contains the list of all curently active layers, the AllyRoot directive and a dictionary of all AllyObject objects.
     * 
     * @see AllyLayer
     * @see AllyRoot
     */
    public global: {layers: {rootObject: AllyObject, previousObject: AllyObject|null}[], root: AllyRoot, objects: {[key:string]: AllyObject}};
    /**
     * The global object for the AllyObject class.
     * 
     * It is shared between all AllyObject objects.
     * 
     * It contains the list of all curently active layers, the AllyRoot directive and a dictionary of all AllyObject objects.
     * 
     * @see AllyLayer
     * @see AllyRoot
     */
    private static global: AllyObject["global"] = {layers: [], root: undefined as unknown as AllyRoot, objects: {}};
    /** Text and comment nodes cannot carry the adx-ally-id attribute. */
    private static attributelessObjects = new WeakMap<AllyNode, AllyObject>();
    /**
     * The AllyEventListener object for the AllyObject object.
     * 
     * @see AllyEventListener
     */
    public eventListener: AllyEventListener;

    /**
     * 
     * @param node The node the AllyObject object is to manage.
     * @param bridge The bridge to the parent in the hierarchical tree.
     * @param options
     * An options object. - Available options:
     * 
     * isRoot - should be set to true if this AllyObject object manages the root element of as subtree that was added to the DOM,
     * 
     * root - this may be used to set AllyObject["global"]["root"] to a new AllyRoot directive.
     * 
     * @see AllyBase
     */
    constructor(node: AllyNode, bridge: () => AllyBase, options?: {isRoot?: boolean, root?: AllyRoot}) {
        super(bridge);
        options = options ? options : {};
        options.isRoot = options.isRoot !== undefined ? options.isRoot : true;
        this.scope = undefined as unknown as AllyObject["scope"];
        this.global = AllyObject.global;
        if (options.root) {
            this.global.root = options.root;
        }
        this.eventListener = new AllyEventListener(this.bridge);
        this.node = node;
        this.id = AllyObject.ids.getId();
        if (this.node.setAttribute) {
            this.node.setAttribute("adx-ally-id", this.id);
        } else {
            AllyObject.attributelessObjects.set(this.node, this);
        }
        this.global.objects[this.id] = this;
        this.attribute = new AllyAttribute(this.bridge, this.node);
        this.variables = {};
        this.attributeListeners = [];
        this.childListListeners = [];
        this.resetFunctions = [];
        this.parentObject = null as AllyObject | null;
        this.childObjects = [];
        this.library = AllyObject.library;
        if (this.library.a11y.isIgnore(this.node)) {
            this.functionMatrix = new AllyFunctionMatrix(this.bridge, [[]]);
        } else {
            this.functionMatrix = new AllyFunctionMatrix(this.bridge, AllyObject.elements.getMatrix());
        }
        this.destroyer.addCallback(() => {
            this.resetFunctions.forEach((callback) => {
                callback();
            });
            this.resetFunctions = [];
            if (this.parentObject) {
                let index = this.parentObject.childObjects.indexOf(this);
                if (index !== -1) {
                    this.parentObject.childObjects.splice(index, 1);
                }
            }
            delete this.global.objects[this.id];
            this.node.removeAttribute && this.node.removeAttribute("adx-ally-id");
            if (AllyObject.attributelessObjects.get(this.node) === this) {
                AllyObject.attributelessObjects.delete(this.node);
            }
            AllyObject.ids.returnId(this.id);
        });
        this.updateParentObject();
        this.updateChildObjects();
        if (options.isRoot && this.parentObject) {
            this.parentObject.updateChildObjects();
        }
    }

    /**
     * This fuction executes the run function beginning at the defined row.
     * @param index The number of the row the execution of run should start at.
     * 
     * @see AllyObject["run"]
     */
    public runRoot(index: number) {
        let run = true;
        let i = index;
        while (run) {
            run = this.run({self: this}, {start: {x: i}, end: {x: i}}) && run;
            i++;
        }
    }

    /**
     * This function runs the functionMatrix of the AllyObject object from a start to an end point.
     * 
     * During this execution the elements of a column are all executed then the execution continues at the top of the next column.
     * 
     * It also runs the run functions of this AllyObject objects child objects with the same options as was provided to this function.
     * 
     * Note:
     * 
     * matrix[x][y] x is the column while y is the row.
     * 
     * The start of the matrix is at x = 0, the top of each column is at y = 0.
     * 
     * @param scope
     * The scope is an object that contains variables used in this instance of the run functions execution.
     * 
     * The same scope will be provided to the run functions of the child objects of this AllyObject object.
     * 
     * The scope also contains a reference to this AllyObject object under the key "self".
     * @param options
     * An options object. - Available options:
     * 
     * start - the point in the functionMatrix where the execution of the callbacks saved in the functionMatrix starts.
     * 
     * end - the point in the functionMatrix where the execution of the callbacks saved in the functionMatrix ends.
     * 
     * Note: The x coordinate of the points is the column of the function matrix while y is the row.
     * @returns
     * Returns True if the last callback that was executed was not the last callback of the functionMatrix.
     * 
     * Note: the run function starts other run functions recursively. If any of the other run functions returns True this function will return True aswell.
     * 
     * @see AllyFunctionMatrix
     */
    public run(scope: AllyObject["scope"] & {[key: string]: unknown}, options?: {start?: AllyPoint, end?: AllyPoint}) {
        if (this.destroyed) {
            return false;
        }
        let result = false;
        scope.self = this;
        this.functionMatrix.run(scope, options);
        // Destroyed under a stale parent pointer: never spliced out, attribute store gone.
        const live = this.childObjects.filter((object) => !object.destroyed);
        if (live.length !== this.childObjects.length) {
            console.warn(`[Ally] Dropped ${this.childObjects.length - live.length} destroyed object(s) from <${this.node.localName}>`);
            this.childObjects = live;
        }
        this.childObjects.forEach((object) => {
            result = object.run(scope, options) || result;
        });
        if (options?.end?.x !== undefined && options.end.x < this.functionMatrix.matrix.length) {
            result = true;
        }
        return result;
    }

    /**
     * This function executes the callbacks saved in AllyObject["attributeListeners"].
     */
    public runAttributeListeners() {
        this.attributeListeners.forEach((callback) => {
            callback();
        });
    }

    /**
     * This function executes the callbacks saved in AllyObject["childListListeners"].
     */
    public runChildListListeners() {
        this.childListListeners.forEach((callback) => {
            callback();
        });
    }

    /**
     * This function executes the callbacks saved in AllyObject["resetFunctions"].
     * 
     * Then the rest of this AllyObject object is reset.
     */
    public reset() {
        this.resetFunctions.forEach((callback) => {
            callback();
        });
        this.resetFunctions = [];
        this.functionMatrix.reset();
        this.attributeListeners = [];
        this.childListListeners = [];
        this.variables = {};
        this.updateParentObject();
        this.updateChildObjects();
        this.attribute.reset();
    }

    /**
     * This function will save the AllyObject object managing the parent node of this AllyObject objects node in AllyObject["parentObject"].
     * 
     * If the parent node of this AllyObject objects node is not managed by a AllyObject object a new AllyObject object will be created.
     */
    public updateParentObject() {
        let parentObject = this.getObjectByNode(this.node.parentNode);
        if (parentObject !== null) {
            this.parentObject = parentObject;
        }
    }

    /**
     * This function will save the AllyObject objects managing the child nodes of this AllyObject objects node in AllyObject["childObjects"].
     * 
     * If any of the child nodes of this AllyObject objects node are not managed by a AllyObject object a new AllyObject object will be created for each such node.
     */
    public updateChildObjects() {
        this.childObjects = [];
        this.node.childNodes.forEach((node) => {
            let object = this.getObjectByNode(node);
            if (!object) {
                object = new AllyObject(node, this.bridge, {isRoot: false});
            } else if (object.parentObject && object.parentObject !== this && object.parentObject.variables.subMenu !== object) {
                // A node moved under a parent inserted in the same burst is never reported on its own.
                const previous = object.parentObject.childObjects;
                const index = previous.indexOf(object);
                if (index !== -1) {
                    previous.splice(index, 1);
                }
                object.reparentDestroyer(() => this);
                object.parentObject = this;
            }
            this.childObjects.push(object);
        });
    }
    
    /** Follows a node that was moved to a new parent. */
    public reparent(parentObject: AllyObject) {
        if (this.parentObject === parentObject) {
            return;
        }
        if (this.parentObject) {
            let index = this.parentObject.childObjects.indexOf(this);
            if (index !== -1) {
                this.parentObject.childObjects.splice(index, 1);
            }
        }
        this.reparentDestroyer(() => parentObject);
        this.parentObject = parentObject;
        parentObject.updateChildObjects();
    }

    /**
     * This function returns the AllyObject object managing the provided node.
     * @param node The node the managing object of is to be returned.
     * @returns Returns the managing object or null if no managing object was found.
     */
    public static stats(): {objects: number, layers: number} {
        return {objects: Object.keys(AllyObject.global.objects).length, layers: AllyObject.global.layers.length};
    }

    public getObjectByNode(node: AllyNode|undefined|null) {
        if (node === undefined || node === null) {
            return null;
        }
        if (!node.getAttribute) {
            return AllyObject.attributelessObjects.get(node) ?? null;
        }
        const id = node.getAttribute("adx-ally-id");
        const object = id ? this.global.objects[id] : undefined;
        // A cloned node carries the id of the node it was copied from.
        return object && object.node === node ? object : null;
    }
}